import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import SetsTable from '../SetsTable';
import Loading from '../Loading';
import LoadMoreBtn from '../LoadMoreBtn';
import ListFilter from '../ListFilter';
import FollowedFilter from '../FollowedFilter';
import { useApi } from '../../context/ApiProvider';
import { Typography, Box } from '@mui/material';

// Module-level cache to persist loaded data across component remounts
// Key: dataFrom, Value: { sets, pagination }
const dataCache = new Map();

const SetsSection = ({
  dataFrom,
  title,
  hideTableInteraction,
  hideFilter,
  showFavoritesFilter,
}) => {
  // Initialize state from cache if available to prevent flickering on remount
  const cachedData = hideFilter ? dataCache.get(dataFrom) : null;
  const [loading, setLoading] = useState(false);
  const [sets, setSets] = useState(cachedData?.sets || []);
  const [pagination, setPagination] = useState(cachedData?.pagination || {});
  const [loadingMore, setLoadingMore] = useState(false);
  const [page, setPage] = useState(1);
  const [paginationConfig, setPaginationConfig] = useState({
    order_by: 'set_meta.release_date',
    order_direction: 'desc',
  });
  // Store paginationConfig in ref to avoid including it in dependency array when in static mode
  const paginationConfigRef = useRef(paginationConfig);
  paginationConfigRef.current = paginationConfig;
  const [favoritesFilter, setFavoritesFilter] = useState(false);

  const api = useApi();

  // Track previous values to prevent unnecessary API calls
  // Separate refs for static and dynamic modes to avoid interference
  const staticDataFromRef = useRef(null);
  const dynamicDataFromRef = useRef(null);
  const prevPaginationConfigRef = useRef(null);
  const prevFavoritesFilterRef = useRef(null);
  // Track if we're currently loading to prevent duplicate calls
  const isLoadingRef = useRef(false);

  // Static mode: only load when dataFrom changes (Track page)
  // In static mode, we don't use paginationConfig or favoritesFilter
  useEffect(() => {
    // Only run in static mode
    if (!hideFilter) return;

    // Check cache first - if data is cached, use it immediately
    // This prevents API calls when component remounts due to Play context updates
    const cachedData = dataCache.get(dataFrom);
    if (cachedData) {
      // Only update state if dataFrom changed or if sets are empty (component remounted)
      if (staticDataFromRef.current !== dataFrom || sets.length === 0) {
        setSets(cachedData.sets);
        setPagination(cachedData.pagination);
        staticDataFromRef.current = dataFrom;
      }
      return; // Use cached data, don't make API call
    }

    // Only load if dataFrom has actually changed
    // This prevents API calls when component re-renders due to Play context updates
    if (staticDataFromRef.current === dataFrom) {
      return; // Already loaded for this dataFrom, don't call API again
    }

    // Prevent duplicate calls if already loading
    if (isLoadingRef.current) {
      return;
    }

    // Store the dataFrom and load ONCE
    staticDataFromRef.current = dataFrom;
    isLoadingRef.current = true;

    // Inline load for static mode - no paginationConfig or favoritesFilter
    (async () => {
      setLoading(true);
      try {
        const response = await api.get(dataFrom, {
          order_by: 'set_meta.release_date',
          order_direction: 'desc',
        });
        if (response.ok) {
          const data = response.body.data ? response.body.data : response.body;
          setSets(data);
          setPagination(response.body.pagination);
          setPage(1);
          // Cache the data for future remounts
          dataCache.set(dataFrom, {
            sets: data,
            pagination: response.body.pagination,
          });
        } else {
          console.log(response);
        }
      } finally {
        setLoading(false);
        isLoadingRef.current = false;
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hideFilter, dataFrom]); // Intentionally exclude api from deps - it's stable

  // Dynamic mode: check if dependencies actually changed
  // Only run when hideFilter is false (filters/sorting enabled)
  useEffect(() => {
    // Early return if in static mode - don't run at all
    if (hideFilter) return;

    const dataFromChanged = dynamicDataFromRef.current !== dataFrom;
    const favoritesFilterChanged =
      prevFavoritesFilterRef.current !== favoritesFilter;

    // Only load if dataFrom or favoritesFilter changed
    // paginationConfig changes are handled in onFilter function
    if (
      dynamicDataFromRef.current === null ||
      dataFromChanged ||
      favoritesFilterChanged
    ) {
      dynamicDataFromRef.current = dataFrom;
      prevFavoritesFilterRef.current = favoritesFilter;
      loadSets();
    }
    // Note: We don't include paginationConfig in deps to prevent runs in static mode
    // paginationConfig changes are handled manually in onFilter function
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hideFilter, dataFrom, favoritesFilter]); // loadSets is stable, api is stable

  function onFilter(genre, sortBy, sortDirection) {
    const newPaginationConfig = {
      ...paginationConfig,
      genre: genre,
      order_direction: sortDirection,
      order_by: `set_meta.${sortBy}`,
    };
    setPaginationConfig(newPaginationConfig);
    // In dynamic mode, manually trigger loadSets when filter changes
    if (!hideFilter) {
      prevPaginationConfigRef.current = JSON.stringify(newPaginationConfig);
      loadSets();
    }
  }

  const handleFavoritesFilterChange = (event) => {
    const newFavoritesFilter = event.target.checked;
    setFavoritesFilter(newFavoritesFilter);
  };

  async function loadSets() {
    setLoading(true);
    const endpoint = favoritesFilter
      ? dataFrom.replace('/sets', '/sets/favs')
      : dataFrom;
    const response = await api.get(endpoint, paginationConfig);
    if (response.ok) {
      const data = response.body.data ? response.body.data : response.body;
      setSets(data);
      setPagination(response.body.pagination);
      setPage(1);
    } else {
      console.log(response);
    }
    setLoading(false);
  }

  async function loadMore() {
    setLoadingMore(true);
    const endpoint = favoritesFilter
      ? dataFrom.replace('/sets', '/sets/favs')
      : dataFrom;
    const response = await api.get(endpoint, {
      ...paginationConfig,
      offset: 20 * page,
    });
    if (response.ok) {
      setSets([...sets, ...response.body.data]);
      setPagination(response.body.pagination);
      setPage(page + 1);
    } else {
      console.log(response);
    }
    setLoadingMore(false);
  }

  return (
    <>
      {title && (
        <Typography variant="h5" color={'white'}>
          {title}
        </Typography>
      )}
      {loading && (
        <Box textAlign="center">
          <Loading alterColor />
        </Box>
      )}
      <Box mb={6.5} textAlign="center" display={loading ? 'none' : 'block'}>
        <Box mb={2}>
          {!hideFilter && (
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                mb: 2,
              }}
            >
              {showFavoritesFilter && (
                <FollowedFilter
                  checked={favoritesFilter}
                  onChange={handleFavoritesFilterChange}
                  mode="favorites"
                />
              )}
              <Box sx={{ display: 'flex', alignItems: 'center', marginTop: 2 }}>
                <ListFilter onFilter={onFilter} listType="sets" />
              </Box>
            </Box>
          )}
          <SetsTable
            items={sets}
            onTableInteraction={setPaginationConfig}
            tableInteractionConfig={paginationConfig}
            hideTableInteraction={hideTableInteraction}
          />
        </Box>
        <Box textAlign="center">
          <LoadMoreBtn
            pagination={pagination}
            onLoadMoreClick={loadMore}
            loading={loadingMore}
          />
        </Box>
      </Box>
    </>
  );
};

SetsSection.propTypes = {
  dataFrom: PropTypes.string,
  title: PropTypes.string,
  hideTableInteraction: PropTypes.bool,
  hideFilter: PropTypes.bool,
  showFavoritesFilter: PropTypes.bool,
};

// Memoize component to prevent re-renders when props haven't changed
export default React.memo(SetsSection);
